import React, {
    FormEvent,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { dynamicRequest } from '../../utils/api-utils/api-utils';
import { checkIfTabletOrMobile, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { InputSocialSecurity } from 'component-library/index';

import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import { Icon as TIcon } from '../../types/shared/Icon';
import errorIcon from '../../assets/images/error-icon.svg';

import './CardActivation.css';
import SessionStorage from '../../utils/SessionStorage';
import { Icon, Title, Button, Section } from '../../importCL';
import InputCreditCard from './components/InputCreditCard';

interface ICardActivationProps extends MappedComponentProperties {
    title?: string;
    instructionText?: string;
    cardNumberLabel?: string;
    cardNumberValidation?: string;
    ssnLabel?: string;
    ssnValidation?: string;
    ssnTooltip?: string;
    ctaLabel?: string;
    helpContainerTopText?: string;
    helpContainerBottomText?: string;
    successIcon?: TIcon;
    successTitle?: string;
    successDescription?: string;
    successCtaLabel?: string;
    successRedirect?: string;
    successHelpContainerTopText?: string;
    successHelpContainerBottomText?: string;
    invalidCardMessage?: string;
    invalidSsnMessage?: string;
    invalidCardAndSsnMessage?: string;
    noMatchErrorMessage?: string;
    noMatchErrorDescription?: string;
    tooManyAttemptsErrorMessage?: string;
    tooManyAttemptsErrorDescription?: string;
    tooManyInvalidRequestsErrorMessage?: string;
    tooManyInvalidRequestsErrorDescription?: string;
    unavailableErrorMessage?: string;
    unavailableErrorDescription?: string;
    systemErrorMessage?: string;
    systemErrorDescription?: string;
    noAccountErrorMessage?: string;
    noAccountErrorDescription?: string;
    systemErrorCtaLabel?: string;
}

interface IActivationResponse {
    responseCode: number;
    message: string;
}

type TFormErrorModel = {
    message: string;
    description: string;
};

enum FormState {
    INITIAL,
    INVALID_CARD,
    INVALID_SSN,
    INVALID_CARD_AND_SSN,
    LOCKED,
    NO_MATCH,
    SERVER_ERROR,
    SUCCESS,
    UNAVAILABLE,
    TOO_MANY_INVALID_REQUESTS,
    NO_ACCOUNT,
}

export const CardActivationEditConfig = {
    emptyLabel: 'Card Activation',
    isEmpty: (props: PropsWithChildren<ICardActivationProps>) => {
        const checkForProps = (
            props: PropsWithChildren<ICardActivationProps>,
        ): boolean => {
            const requiredProps = [
                'title',
                'instructionText',
                'cardNumberLabel',
                'ssnLabel',
                'ctaLabel',
            ];
            for (const requiredProp of requiredProps) {
                // eslint-disable-next-line
                if (!props.hasOwnProperty(requiredProp)) {
                    return true;
                }
            }
            return false;
        };
        return checkForProps(props);
    },
};

const CardActivation = (
    props: PropsWithChildren<ICardActivationProps>,
): JSX.Element => {
    const [cardValue, setCardValue] = useState<string>('');
    const [cardIsValid, setCardIsValid] = useState<boolean>(true);
    const [ssnValue, setSsnValue] = useState<string>('');
    const [validSsn, setValidSsn] = useState<boolean>(true);
    const [formState, setFormState] = useState<FormState>(0);
    const [invalidSubmissionCount, setInvalidSubmissionCount] = useState<number>(1);
    const activationRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLInputElement>(null);
    const ssnRef = useRef<HTMLInputElement>(null);
    const { width } = useWidthContext();

    const setBottomPadding = () => {
        const renderedApp = document.getElementById('spa-root');

        if (!renderedApp || !activationRef || !activationRef.current) {
            return;
        }

        const renderedHeight = renderedApp.getBoundingClientRect().height;
        const screenHeight = window.screen.height;

        if (screenHeight > renderedHeight) {
            const currentBottomPadding = parseInt(
                window
                    .getComputedStyle(activationRef.current, null)
                    .getPropertyValue('padding-bottom')
                    .replace('px', ''),
            );
            const heightDiff = screenHeight - renderedHeight;
            
            //DPM-10226 Side Effect: Hard code footer + header height 
            const newBottomPadding = currentBottomPadding + heightDiff - 630;
            activationRef.current.style.paddingBottom = `${newBottomPadding}px`;
        }
    };

    const invalidFormCounter = (): void => {
        if (invalidSubmissionCount === 2) {
            setCardValue('');
            setSsnValue('');
            setValidSsn(false);
            setFormState(3);
        }
        if (invalidSubmissionCount >= 3) {
            setFormState(9);
        }

        setInvalidSubmissionCount(invalidSubmissionCount + 1);
    };

    const handleError = (response: any): void => {
        if (response.status === 401) {
            setCardValue('');
            setSsnValue('');
            return setFormState(5);
        }
        if (response.status === 422) {
            setCardValue('');
            setSsnValue('');
            return setFormState(10);
        }
        if (response.status === 423) {
            return setFormState(4);
        }
        if ([404, 500].includes(response.status)) {
            return setFormState(8);
        }
        return setFormState(6);
    };

    const sendApiRequest = async (): Promise<void> => {
        const requestObj = {
            cardNumber: cardValue,
            last4SSN: ssnValue,
            ipAddress: SessionStorage.tryGet('protocol', ''),
            source: checkIfTabletOrMobile() ? 'MOBILE Web' : 'WEB',
        };

        const response = await dynamicRequest<IActivationResponse>(
            'online-activation',
            {
                requestType: 'post',
                body: requestObj,
            },
        );

        if (response.status === 200) {
            setFormState(7);
            return;
        }

        handleError(response);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        window.scroll(0, 0);
        if (!cardIsValid || !cardValue) {
            setCardIsValid(false);
            setFormState(ssnValue.length === 4 ? 1 : 3);
            invalidFormCounter();
            return;
        }
        if (ssnValue.length !== 4) {
            setFormState(2);
            invalidFormCounter();
            return;
        }

        setInvalidSubmissionCount(1);
        sendApiRequest().catch((e: any) => {
            handleError(e.response);
        });
    };

    const removeTelLinkOnDesktop = (input: ReactElement[]): ReactElement[] => {
        return input.map(el => {
            if (
                typeof el === 'object' &&
                el.type === 'a' &&
                el.props &&
                el.props.href &&
                el.props.href.startsWith('tel')
            ) {
                return (
                    <React.Fragment key={el.key}>{el.props.children}</React.Fragment>
                );
            }
            return el;
        });
    };

    const resetForm = (): void => {
        setFormState(0);
        setCardValue('');
        setSsnValue('');
    };

    const getFormError = (
        formState: FormState,
        props: PropsWithChildren<ICardActivationProps>,
    ): JSX.Element => {
        const errorMap: Record<number, TFormErrorModel> = {
            1: {
                message: props.invalidCardMessage || '',
                description: '',
            },
            2: {
                message: props.invalidSsnMessage || '',
                description: '',
            },
            3: {
                message: props.invalidCardAndSsnMessage || '',
                description: '',
            },
            4: {
                message: props.tooManyAttemptsErrorMessage || '',
                description: props.tooManyAttemptsErrorDescription || '',
            },
            5: {
                message: props.noMatchErrorMessage || '',
                description: props.noMatchErrorDescription || '',
            },
            6: {
                message: props.systemErrorMessage || '',
                description: props.systemErrorDescription || '',
            },
            8: {
                message: props.unavailableErrorMessage || '',
                description: props.unavailableErrorDescription || '',
            },
            9: {
                message: props.tooManyInvalidRequestsErrorMessage || '',
                description: props.tooManyInvalidRequestsErrorDescription || '',
            },
            10: {
                message: props.noAccountErrorMessage || '',
                description: props.noAccountErrorDescription || '',
            },
        };

        return (
            <div
                className={`CardActivation__formElement--errorAlert p-4 flex flex-row items-start rounded border border-solid border-feedback-error bg-feedback-error-soft gap-[0.1875rem] ${[4, 8, 9].includes(formState) ? 'mb-12' : ''
                }`}
            >
                <div className='CardActivation__errorIcon flex-none rotate-180'>
                    <Icon loading='lazy' src={errorIcon} size='utility' />
                </div>

                <div className='CardActivation__formElement--errors pl-2 md:pl-0'>
                    <p className='CardActivation__errorMessage m-0 font-bold text-feedback-error pt-[0.1875rem]'>
                        {errorMap[formState].message.replace(/\u2028/g, '')}
                    </p>
                    {errorMap[formState].description && (
                        <p className='CardActivation__errorDescription'>
                            {width > 768
                                ? removeTelLinkOnDesktop(
                                    transformHTMLParserLinks(errorMap[formState].description),
                                )
                                : transformHTMLParserLinks(errorMap[formState].description)}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    // trigger DCR for datalayer on activation success
    useEffect(() => {
        if (formState === 7) {
            window.dispatchEvent(new CustomEvent('event242'));
        }
    }, [formState]);

    return (
        <div id='card-activation' className='CardActivation bg-[#e8e8e8] pb-6 pt-20 md:pt-6' ref={activationRef}>
            <Section className='!p-0' onLoad={() => setBottomPadding()}>
                <form className='bg-white my-0 mx-auto flex flex-col gap-6 py-12 px-10 border border-solid border-grey-silver rounded max-w-[30rem] md:max-w-full md:mx-4 md:py-8 md:px-6' onSubmit={handleSubmit}>
                    {formState === 7 ? (
                        <React.Fragment>
                            <Icon
                                src={props.successIcon?.iconPath}
                                loading='lazy'
                                size='badge'
                                alt={props.successIcon?.iconAltText}
                                className='block mt-12 mb-0 mx-auto'
                            />
                            <Title
                                tag='h1'
                                verticalBar
                                align='center'
                                className='h2 mb-0'
                            >
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.successTitle))}
                            </Title>
                            <p className='my-0 mx-auto text-center pb-6 max-w-[12.1875rem]'>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.successDescription?.replace(/\u2028/g, '')))}
                            </p>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Title
                                tag='h1'
                                verticalBar
                                align='left'
                                className='h2 mb-0'
                            >
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                            </Title>
                            {formState !== 0 && getFormError(formState, props)}
                            {![4, 6, 8, 9].includes(formState) && (
                                <React.Fragment>
                                    <p className='mb-0'>
                                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.instructionText))}
                                    </p>
                                    <InputCreditCard
                                        name='CardNumber'
                                        label={transformHTMLParserLinks(wrapTrademarkSuperscript(props.cardNumberLabel))}
                                        value={cardValue}
                                        setValue={setCardValue}
                                        valid={[cardIsValid, setCardIsValid]}
                                        ref={cardRef}
                                        invalidErrorMessage={
                                            props.cardNumberValidation ||
                                            'Please enter a 15- or 16-digit number'
                                        }
                                        requiredMessage={
                                            props.cardNumberValidation ||
                                            'Please enter a 15- or 16-digit number'
                                        }
                                        onFocus={() => {
                                            setCardIsValid(true);
                                        }}
                                        required
                                        removeAsterisk
                                    />
                                    <InputSocialSecurity
                                        className='w-[66%] mb-[0.125rem]'
                                        label={transformHTMLParserLinks(wrapTrademarkSuperscript(props.ssnLabel)) || ''}
                                        name='Ssn'
                                        lastFour
                                        ref={ssnRef}
                                        autoComplete='off'
                                        id='ssn'
                                        type='password'
                                        value={ssnValue}
                                        setValue={setSsnValue}
                                        valid={[validSsn, setValidSsn]}
                                        invalidErrorMessage={
                                            props.ssnValidation ||
                                            'Please enter the last 4 digits of your Social Security Number'
                                        }
                                        required
                                        requiredMessage={
                                            props.ssnValidation ||
                                            'Please enter the last 4 digits of your Social Security Number'
                                        }
                                        tooltip={props.ssnTooltip}
                                        tooltipNotchPosition='bottom-center'
                                        removeAsterisk
                                    />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                    {formState === 7 && (
                        <Button
                            size='large'
                            href={
                                props.successRedirect ||
                                '/content/cob-corp-acquisition/us/en/index.html'
                            }
                            className='CardActivation__button'
                        >
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.successCtaLabel)) as unknown as ReactElement}
                        </Button>
                    )}
                    {formState === 6 && (
                        <Button
                            size='large'
                            className='CardActivation__button'
                            onClick={resetForm}
                        >
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.systemErrorCtaLabel)) as unknown as ReactElement}
                        </Button>
                    )}
                    {![4, 6, 7, 8, 9].includes(formState) && (
                        <Button
                            size='large'
                            type='submit'
                            name='CardActivationSubmit'
                            className='CardActivation__button'
                        >
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.ctaLabel)) as unknown as ReactElement}
                        </Button>
                    )}
                </form>
            </Section>
            <div className='max-w-[30rem] text-center my-10 mx-auto'>
                <p className='font-bold m-0'>
                    {formState === 7
                        ? transformHTMLParserLinks(wrapTrademarkSuperscript(props.successHelpContainerTopText))
                        : transformHTMLParserLinks(wrapTrademarkSuperscript(props.helpContainerTopText))}
                </p>
                <p>
                    {transformHTMLParserLinks(
                        (formState === 7
                            ? props.successHelpContainerBottomText
                            : props.helpContainerBottomText) || '',
                    )}
                </p>
            </div>
        </div>
    );
};

export default CardActivation;
